/**
 * Adaptateur moteur CTVO v1 → findings UI agence.
 *
 * Symétrique de `auditVerdictAdapter.ts` (qui adapte l'audit batch Opus
 * transitoire). Ici on adapte le résultat du moteur v1 (SPEC §7) — un
 * objet structuré contenant un tableau `controles[]` avec `statut`,
 * `detail`, `action`, `sources`. Chaque contrôle devient un finding
 * réutilisable par `AlertsColumn`.
 *
 * Cascade prévue dans FormCGIAv2 quand le flag form_cgia est ON :
 *   v1 (si disponible) → audit batch Opus (fallback transitoire) → regex (filet)
 */

import type { CgDocType } from '../ocr/ocrResponseTypes';
import type { CrossCheckFinding, Severity } from '../validation/crossCheckEngine';

import type {
  CtvoAnalyzeV1Result,
  CtvoControleStatut,
  CtvoDocType,
  CtvoStatutGlobal,
} from '../../../types/ctvoAnalyzeV1';

const statutToSeverity = (statut: CtvoControleStatut): Severity | null => {
  switch (statut) {
    case 'bloquant':
      return 'error';
    case 'attention':
      return 'warning';
    case 'ok':
      // Pas de finding pour les contrôles OK — AlertsColumn n'affiche que
      // ce qui demande une action. Le compteur "OK" est dans synthese.nb_ok.
      return null;
    default:
      return null;
  }
};

/**
 * Le moteur v1 a un type doc plus large (inclut 'autre') que le type
 * historique CgDocType utilisé par AlertsColumn. On filtre les valeurs
 * incompatibles plutôt que de risquer une erreur de typage runtime.
 */
const toCgDocTypes = (sources: CtvoDocType[] | undefined): CgDocType[] | undefined => {
  if (!sources || sources.length === 0) return undefined;
  const KNOWN_CG_DOC_TYPES: readonly string[] = [
    'carte_grise',
    'cerfa_cession',
    'cerfa_demande_immat',
    'mandat',
    'recepisse_da',
    'controle_technique',
    'assurance',
    'piece_identite',
    'piece_identite_hebergeur',
    'piece_identite_cotitulaire',
    'permis_conduire',
    'kbis',
  'kbis_vendeur',
  'kbis_acheteur',
    'justif_domicile',
    'attestation_hebergement',
    'acte_deces',
    'justif_succession',
  ];
  const filtered = sources.filter((s) => KNOWN_CG_DOC_TYPES.includes(s)) as unknown as CgDocType[];
  return filtered.length > 0 ? filtered : undefined;
};

/**
 * Convertit un résultat moteur v1 en liste de findings UI.
 * - controles[].statut === 'bloquant' → severity `error`
 * - controles[].statut === 'attention' → severity `warning`
 * - controles[].statut === 'ok' → pas de finding (compté côté synthese)
 *
 * Le message du finding combine `libelle` (titre court, gras dans
 * AlertsColumn) + `detail` (corps explicatif). L'`action` est ajoutée
 * en fin de message quand disponible.
 *
 * `ruleId` est préfixé `ctvo_v1_` pour distinguer en trace/debug d'un
 * finding regex ou audit batch.
 */
export const ctvoV1ResultToFindings = (result: CtvoAnalyzeV1Result | null): CrossCheckFinding[] => {
  if (!result) return [];

  return result.controles
    .map((controle): CrossCheckFinding | null => {
      const severity = statutToSeverity(controle.statut);
      if (!severity) return null;

      const actionSuffix = controle.action ? ` — Action : ${controle.action}` : '';
      const message = `${controle.libelle} — ${controle.detail}${actionSuffix}`;

      return {
        ruleId: `ctvo_v1_${controle.id}`,
        severity,
        message,
        documentTypes: toCgDocTypes(controle.sources),
      };
    })
    .filter((f): f is CrossCheckFinding => f !== null);
};

/**
 * Statut métier dérivé du verdict v1, aligné sur la cascade FormCGIAv2.
 * Le statut 'analyse_echouee' renvoie 'inconnu' pour que la cascade
 * descende vers l'Opus / regex (filet).
 */
export const ctvoV1StatutToStatus = (
  statut: CtvoStatutGlobal | undefined,
): 'pret' | 'attention' | 'bloque' | 'inconnu' => {
  switch (statut) {
    case 'pret':
      return 'pret';
    case 'attention':
      return 'attention';
    case 'bloque':
      return 'bloque';
    case 'analyse_echouee':
    default:
      return 'inconnu';
  }
};

/**
 * Mappe `result.dossier.parcours` du moteur v1 vers le libellé de la
 * bannière FormCGIAv2 (cohérent avec auditVerdictAdapter pour le moteur
 * batch existant).
 */
export const ctvoV1ParcoursToBannerLabel = (result: CtvoAnalyzeV1Result | null): string | null => {
  if (!result) return null;
  switch (result.dossier.parcours) {
    case 'parcours_a':
      return "Parcours A — cession par un professionnel intermédiaire (récépissé de déclaration d'achat requis)";
    case 'succession':
      return 'Succession — pièces spécifiques requises';
    case 'autre':
      return result.dossier.parcours_justification
        ? `Parcours spécifique — ${result.dossier.parcours_justification}`
        : 'Parcours spécifique détecté';
    case 'classique':
    default:
      return null;
  }
};
